import { SiteInfo } from "@/globals";
import { getBrandsByStatus } from "@/js/repository/BrandRepository";
import Head from "next/head";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { getCategoriesByProducts } from "@/js/repository/CategoryRepository";
import { groupBrandsByCategory } from "@/js/constants/categoryGroups";
import { AdSenseBanner } from "@/components/app/AdSenseBanner";
import { useIsMobile } from "@/lib/useIsMobile";

export async function getServerSideProps(context: any) {
  const { locale } = context;
  const siteUrl = `https://${context.req.headers.host}`;
  
  const brands = await getBrandsByStatus(locale, 'visible');
  const categories = await getCategoriesByProducts(locale);
  
  return {
    props: { brands, siteUrl, categories },
  };
}

export default function Brands(props: any) {
  const { t } = useTranslation();
  const { brands } = props;
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  const groupedBrands = groupBrandsByCategory(brands);

  return (
    <>
      <Head>
        <title>{`${t("brands")} | ${SiteInfo.siteName}`}</title>
        <meta name="description" content={`${t("brandsDescription")} | ${SiteInfo.siteName}`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${props.siteUrl}/brands`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${props.siteUrl}/brands`} />
        <meta property="og:title" content={`${t("brands")} | ${SiteInfo.siteName}`} />
        <meta property="og:description" content={`${t("brandsDescription")} | ${SiteInfo.siteName}`} />
        <meta property="og:image" content={`${props.siteUrl}/logo.png`} />
        <meta property="og:site_name" content={SiteInfo.siteName} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${props.siteUrl}/logo.png`} />
        <meta name="twitter:title" content={`${t("brands")} | ${SiteInfo.siteName}`} />
        <meta name="twitter:description" content={`${t("brandsDescription")} | ${SiteInfo.siteName}`} />

        <script type="application/ld+json" dangerouslySetInnerHTML={
          {
            __html: JSON.stringify(
            {
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": t("brands"),
              "description": `${t("brandsDescription")} | ${SiteInfo.siteName}`,
              "url": `${props.siteUrl}/brands`,
              "numberOfItems": brands.length
            }, null, 2)
          }
        }>
        </script>
      </Head>

      <div className="mb-8">
        <section className="mb-6 rounded-lg border border-black/10 bg-white p-5 shadow-sm sm:p-6">
          <p className="text-xs font-black uppercase tracking-normal text-[#fd384f]">{t("curatedByProsense")}</p>
          <h1 className="mt-2 text-2xl font-black text-[#101820] sm:text-3xl">{t("brands")}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">{t("brandsDescription")}</p>
        </section>
        
        {/* 카테고리 그룹 탭 */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${activeGroup === null ? 'bg-[#101820] text-white' : 'border border-black/10 bg-white text-gray-700 hover:border-[#fd384f]/40 hover:text-[#fd384f]'}`}
            onClick={() => setActiveGroup(null)}
          >
            {t("brands.allBrands")}
          </button>
          {groupedBrands.map(group => (
            <button
              key={group.id}
              className={`rounded-lg px-3 py-2 text-sm font-semibold ${activeGroup === group.id ? 'bg-[#101820] text-white' : 'border border-black/10 bg-white text-gray-700 hover:border-[#fd384f]/40 hover:text-[#fd384f]'}`}
              onClick={() => setActiveGroup(group.id)}
            >
              {t(group.name)} ({group.brands?.length || 0})
            </button>
          ))}
        </div>

        <div className="my-6">
          <AdSenseBanner id="brands-content-top" slot="4220411723" height={isMobile ? 100 : 120} />
        </div>

        {/* 브랜드 리스트 */}
        {activeGroup === null ? (
          // 모든 브랜드 표시
          <div>
            {groupedBrands.map(group => (
              <div key={group.id} className="mb-8">
                <h2 className="text-xl font-semibold mb-4">{t(group.name)}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {group.brands?.map(brand => (
                    <a 
                      key={brand.name} 
                      href={`/brand/${encodeURIComponent(brand.name.toLowerCase())}`}
                      className="rounded-lg border border-black/10 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-[#fd384f]/40 hover:shadow-lg"
                    >
                      {brand.topProduct && (
                        <div className="mb-3 aspect-square overflow-hidden rounded-md bg-[#f2efe8]">
                          <img 
                            src={brand.topProduct.imageUrl} 
                            alt={brand.name} 
                            className="h-full w-full object-contain"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="font-bold text-[#101820]">{brand.name}</div>
                      <div className="text-sm text-gray-500">{brand.count} {t("products")}</div>
                      {brand.categories && brand.categories.length > 0 && (
                        <div className="mt-1 overflow-hidden">
                          <span className="block truncate rounded-md bg-[#fff1f2] px-2 py-1 text-xs font-semibold text-[#fd384f]">
                            {brand.categories[0]}
                          </span>
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // 선택된 카테고리 그룹만 표시
          <div>
            {groupedBrands
              .filter(group => group.id === activeGroup)
              .map(group => (
                <div key={group.id}>
                  <h2 className="text-xl font-semibold mb-4">{t(group.name)}</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {group.brands?.map(brand => (
                      <a 
                        key={brand.name} 
                        href={`/brand/${encodeURIComponent(brand.name.toLowerCase())}`}
                        className="rounded-lg border border-black/10 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-[#fd384f]/40 hover:shadow-lg"
                      >
                        {brand.topProduct && (
                          <div className="mb-3 aspect-square overflow-hidden rounded-md bg-[#f2efe8]">
                            <img 
                              src={brand.topProduct.imageUrl} 
                              alt={brand.name} 
                              className="h-full w-full object-contain"
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="font-bold text-[#101820]">{brand.name}</div>
                        <div className="text-sm text-gray-500">{brand.count} {t("products")}</div>
                        {brand.categories && brand.categories.length > 0 && (
                          <div className="mt-1 overflow-hidden">
                            <span className="block truncate rounded-md bg-[#fff1f2] px-2 py-1 text-xs font-semibold text-[#fd384f]">
                              {brand.categories[0]}
                            </span>
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="my-6">
        <AdSenseBanner id="brands-content-bottom" slot="4220411723" height={isMobile ? 180 : 240} />
      </div>
    </>
  );
}
